// src/encounter/ap-slash-add-line.tsx
//
// Backslash insert (switch `slashInsert`, `?x=slash-insert`; OFF by default, ON on the dev deploy). A physician types "\" and a
// trigger word ("\med amox", "\dx diab", "\vax", "\lab", "\ref", "\fav") and gets ONE kind of item to add, instead of the
// mixed search.
//
// BUILT FROM THE EXISTING LINE: the field, the menu, the keyboard, the live region and the states are `ApAddLine`; the
// data is `useApCategorySearch` (provider favourites + the master catalogue per category), the same hook the Diagnosis and
// Order lines use; the rows and the `AddLinePick` values are built as `ApDiagnosisAddLine` / `ApOrderAddLine` build them.
//
// NO NEW WRITE PATH: a pick goes to `onPickDiagnosis` (a diagnosis) or `onPickOrder` (every other pick), the handlers the
// A&P view already gives the two existing lines, and only on the physician's click or Enter on the highlighted row.
// Typing alone calls nothing.
//
// The rules:
//   · "\" alone or an unknown word shows NO search, only the hint grid of the triggers. An unknown word never falls back
//     to a mixed search;
//   · a known kind searches ONLY that kind; a kind the user may not order says "You can't add <kind> here" and searches nothing;
//   · "\fav" lists the provider's favourites of every allowed kind (filtered by the text after it). Favourites only, no catalogue;
//   · "\ref" (only with the `referrals` switch on) lists the clinic's SPECIALITIES at once, with no text, so the physician
//     does not type "Dietitian" (PROD-3336). One org read (services/use-org-specialities.ts), filtered in memory by the
//     text; if it fails or is empty, the Dietitian row is offered. A pick opens the referral dialog, the
//     same path as a referral picked on "Add order" (#749).
import { useState } from 'react';
import type { ApSearchItem, OrderType as DsOrderType } from '@scripsteam/scrips-signal-ds';
import { useApCategorySearch } from './services/use-ap-category-search';
import {
  ADD_LINE_DEBOUNCE_MS,
  ApAddLine,
  DIAGNOSES_NO_MATCH,
  NO_FAVOURITES_YET,
  ORDERS_NO_MATCH,
  ApOrderAddLine,
  type AddLineEntry,
  type AddLinePick,
  type ApOrderAddLineProps,
} from './ap-add-line';
import { MIN_CATALOGUE_CHARS, ORDER_SEARCH, announcePick, failureText, flat, hintOf, lineStatus, norm, row, useRetryFailed, useSettled } from './lib/add-line-helpers';
import { liveSections, withoutCodes, type AddLineSection } from './lib/add-line-sections';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { SPECIALITY_SOURCE, specialityRowHint } from './lib/referral-specialities';
import { parseSlashQuery, shownTriggers, slashKindsFor, type SlashKindEntry, type SlashKind } from './lib/slash-trigger';

export const SLASH_LINE_LABEL = 'Add with \\';
export const SLASH_NO_FAVOURITES_MATCH = 'No favourites match';
const slashCannotAdd = (label: string): string => `You can't add ${label.toLowerCase()} here`;

type Category = 'diagnosis' | DsOrderType;
/** The categories each searchable kind reads: one, except "\ord" (every order kind this line adds). */
const CATEGORIES_OF: Record<Exclude<SlashKind, 'favourites'>, readonly Category[]> = {
  medication: ['medication'],
  diagnosis: ['diagnosis'],
  vaccination: ['vaccination'],
  lab: ['lab'],
  orders: ['lab', 'imaging', 'procedure'],
  referral: ['referral'],
  // The Diagnoses line's own triggers (`DX_SLASH_KINDS`): never offered on this line, so they read nothing here.
  suggestions: [],
  sets: [],
};
/** Every category a favourites list can hold, in the order the A&P lists them. */
const FAVOURITE_CATEGORIES: readonly Category[] = ['diagnosis', ...ORDER_SEARCH.map((o) => o.type)];
const kindWord = (category: Category): string => (category === 'diagnosis' ? 'Diagnosis' : (ORDER_SEARCH.find((o) => o.type === category)?.kind ?? category));

export interface ApSlashAddLineProps {
  providerId?: string;
  /** `dxOrderSplit`: order items only (no \dx, no diagnosis favourites in \fav). Absent: as before. */
  ordersOnly?: boolean;
  /** The order types this user may place: permission DATA from the host, never decided here. */
  allowedOrderTypes: readonly DsOrderType[];
  /** Codes already on the visit: such a diagnosis row shows as added. */
  existingCodes?: readonly string[];
  /** `<type>:<CODE>` of the orders already on the plan: such a row shows as added. */
  existingOrderKeys?: readonly string[];
  inputId?: string;
  /** The handler `ApDiagnosisAddLine` gets: a diagnosis pick. */
  onPickDiagnosis: (pick: AddLinePick) => void | PromiseLike<unknown>;
  /** The handler `ApOrderAddLine` gets: every other pick. */
  onPickOrder: (pick: AddLinePick) => void | PromiseLike<unknown>;
}

/** The hint grid: each kind and its short triggers. A plain list; it holds no search. */
function SlashHintGrid({ kinds, unknownWord }: { kinds: readonly SlashKindEntry[]; unknownWord?: string }) {
  return (
    <div data-slot="ap-slash-hints" className="flex flex-col gap-1 text-size-sm text-text-secondary">
      {unknownWord ? <p className="m-0">{`No trigger "\\${unknownWord}". Use one of these:`}</p> : null}
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {kinds.map((k) => (
          <li key={k.kind}>
            {k.label} · {shownTriggers(k).map((t) => `\\${t}`).join(' ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ApSlashAddLine(props: ApSlashAddLineProps) {
  const [query, setQuery] = useState('');
  const [fieldOpen, setFieldOpen] = useState(false);
  const { line, below } = useSlashAddLine({ ...props, query, fieldOpen });
  return (
    <div data-slot="ap-slash-add-line" className="flex flex-col gap-1">
      <ApAddLine<AddLinePick>
        label={SLASH_LINE_LABEL}
        inputId={props.inputId}
        query={query}
        onQuery={setQuery}
        onFieldOpenChange={setFieldOpen}
        {...line}
        announceFor={announcePick}
      />
      {below}
    </div>
  );
}

/**
 * THE ONE ORDERS LINE (Samer, 3 Nov call: the Orders block "cannot have multiple components"; Antonin's list: "Advisors: one
 * instance"). The catalog's A&P Plan (scrips-signal-ds feat/signal @5c52a404, AssessmentPlanView `PlanPart`) draws one "Add order"
 * line. With `slashInsert` on, that one line also takes the backslash triggers: text that starts with "\" is the slash search
 * (the triggers, the hint grid, "You can't add <kind> here"), any other text is the Add order search. One field, so focus and
 * the typed text stay when the first "\" switches it. Every pick goes to the same handlers as before: a diagnosis to
 * `onPickDiagnosis`, every other pick to `onPickOrder` (on the plain Add order search, `onPickOrder` too).
 */
export function ApOrderSlashAddLine({
  existingCodes,
  onPickDiagnosis,
  onPickOrder,
  ...orderProps
}: ApSlashAddLineProps & Omit<ApOrderAddLineProps, 'onPick' | 'query' | 'onQuery' | 'onFieldOpenChange' | 'searchQuery' | 'searchFieldOpen' | 'override'>) {
  const [query, setQuery] = useState('');
  const [fieldOpen, setFieldOpen] = useState(false);
  const slash = query.trimStart().startsWith('\\');
  // Both searches always run as hooks; the one not in use gets no text and a closed field, so it reads nothing.
  const slashed = useSlashAddLine({ ...orderProps, existingCodes, onPickDiagnosis, onPickOrder, query: slash ? query.trimStart() : '', fieldOpen: slash && fieldOpen });
  return (
    <div data-slot="ap-slash-add-line" className="flex flex-col gap-1">
      {/* The same `ApOrderAddLine` element in both modes, so the field is never remounted when the first "\" is typed. */}
      <ApOrderAddLine
        {...orderProps}
        onPick={onPickOrder}
        query={query}
        onQuery={setQuery}
        onFieldOpenChange={setFieldOpen}
        searchQuery={slash ? '' : query}
        searchFieldOpen={!slash && fieldOpen}
        override={slash ? slashed.line : undefined}
      />
      {slash ? slashed.below : null}
    </div>
  );
}

/** The backslash search for a query and field state the caller holds: the line's props, and what shows under it (the
 *  "can't add" line and the hint grid). */
function useSlashAddLine({ providerId, allowedOrderTypes, existingCodes = [], existingOrderKeys = [], onPickDiagnosis, onPickOrder, query, fieldOpen, ordersOnly = false }: ApSlashAddLineProps & { query: string; fieldOpen: boolean }) {
  // `referrals` ON: the referral rows are the roles, and "\ref" is offered. Read once per mount, as the Add order line does.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  const kinds = slashKindsFor({ referrals: referralsOn, ordersOnly });
  const parsed = parseSlashQuery(query, kinds);
  const kind = parsed && parsed.kind !== 'menu' && parsed.kind !== 'unknown' ? parsed.kind : undefined;
  const rest = parsed && 'rest' in parsed ? parsed.rest : '';
  const settled = useSettled(rest, ADD_LINE_DEBOUNCE_MS);
  const current = settled.trim() === rest;
  const typedRest = rest.length > 0;

  // `dxOrderSplit`: no diagnosis on the order line, so \fav reads no diagnosis favourites.
  const allowed = new Set<Category>([...(ordersOnly ? [] : (['diagnosis'] as Category[])), ...allowedOrderTypes]);
  // Which categories are read now: the one kind asked for, or every allowed one for "\fav". Never more.
  const reading = new Set<Category>(
    kind === 'favourites' ? FAVOURITE_CATEGORIES.filter((c) => allowed.has(c)) : kind ? CATEGORIES_OF[kind].filter((c) => allowed.has(c)) : [],
  );
  const catalogueOn = kind !== undefined && kind !== 'favourites';
  const pid = (c: Category) => (reading.has(c) ? providerId : undefined);
  const q = (c: Category) => (reading.has(c) ? settled : '');
  // One search per category, always called in the same order (Rules of Hooks); a category not read gets no provider and no
  // text, so its hooks stay idle. `catalogueOn` is false for favourites: those hooks read the provider's list only.
  const diagnosis = useApCategorySearch(pid('diagnosis'), 'diagnosis', q('diagnosis'), catalogueOn);
  // The lab-source probe runs only once this line reads labs (\lab, \ord, \fav), so \ref or \med asks the server nothing about labs.
  const lab = useApCategorySearch(pid('lab'), 'lab', q('lab'), catalogueOn, { labOrderSearch: reading.has('lab') });
  const medication = useApCategorySearch(pid('medication'), 'medication', q('medication'), catalogueOn);
  const imaging = useApCategorySearch(pid('imaging'), 'imaging', q('imaging'), catalogueOn);
  const procedure = useApCategorySearch(pid('procedure'), 'procedure', q('procedure'), catalogueOn);
  const referral = useApCategorySearch(pid('referral'), 'referral', q('referral'), catalogueOn);
  const vaccination = useApCategorySearch(pid('vaccination'), 'vaccination', q('vaccination'), catalogueOn);
  const byCategory = { diagnosis, lab, medication, imaging, procedure, referral, vaccination };

  const retry = useRetryFailed(
    (key) =>
      key[0] === 'provider-favourites' ||
      (['lab-search', 'lab-order-search', 'medication-search', 'master-medication-valuesets', 'master-vaccination-valuesets', 'master-diagnosis-codes'].includes(String(key[0])) &&
        key.some((part) => typeof part === 'string' && part.trim() === rest && rest !== '')),
  );
  const takenDx = new Set(existingCodes.map(norm));
  const takenOrders = new Set(existingOrderKeys.map((k) => k.toUpperCase()));

  const sections: AddLineSection<AddLineEntry<AddLinePick>>[] = [];
  if (current) {
    for (const category of FAVOURITE_CATEGORIES) {
      if (!reading.has(category)) continue;
      const found = byCategory[category];
      const favs = found.items.favourites ?? [];
      // No text, no catalogue (a cleared search may still hold its last answer as placeholder data); "\fav" never has one.
      // "\ref" is the exception: its catalogue is the clinic's speciality list, shown in full before any text.
      const specialities = category === 'referral' && referralsOn;
      const catalogue = !catalogueOn || (!typedRest && !specialities) || found.stale ? [] : withoutCodes(found.items.results, favs.map((f) => f.code));
      const source =
        category === 'diagnosis'
          ? 'ICD-10 catalogue'
          : specialities
            ? SPECIALITY_SOURCE
            : (ORDER_SEARCH.find((o) => o.type === category)?.catalogue ?? 'catalogue');
      const entry = (prefix: string, from: string) => (i: ApSearchItem) =>
        row({
          key: category === 'diagnosis' ? `${prefix}:${i.id}` : `${prefix}:${category}:${i.id}`,
          kind: kindWord(category),
          term: i.label,
          // A speciality row hides its GUID and says when no practitioner is active (PROD-3336).
          hint: specialities && prefix === 'cat' ? specialityRowHint(i.item) : hintOf(i.code, from),
          added: !!i.code && (category === 'diagnosis' ? takenDx.has(norm(i.code)) : takenOrders.has(`${category}:${norm(i.code)}`.toUpperCase())),
          value: { kind: 'item', category, item: i },
        });
      sections.push({ id: `${category}-favourites`, label: `${kindWord(category)} · your favourites`, rows: favs.map(entry('fav', 'your favourites')) });
      sections.push({ id: `${category}-catalogue`, label: `${kindWord(category)} · ${source}`, rows: catalogue.map(entry(category === 'diagnosis' ? 'icd' : 'cat', source)) });
    }
  }
  const entries = flat(liveSections(sections));

  const readList = [...reading];
  const failed = [
    ...(catalogueOn && typedRest ? readList.filter((c) => byCategory[c].error).map(kindWord) : []),
    ...readList.filter((c) => byCategory[c].favouritesError).map((c) => `${kindWord(c)} favourites`),
  ];
  const browsing = readList.length > 0 && !typedRest;
  const status = lineStatus({
    typed: typedRest,
    browsing,
    current,
    loading: readList.some((c) => byCategory[c].loading),
    error: failed.length > 0,
    rows: entries.length,
    answered: kind === 'favourites' ? true : rest.length >= MIN_CATALOGUE_CHARS && readList.every((c) => !byCategory[c].stale),
  });
  const emptyText = browsing ? NO_FAVOURITES_YET : kind === 'favourites' ? SLASH_NO_FAVOURITES_MATCH : kind === 'diagnosis' ? DIAGNOSES_NO_MATCH : ORDERS_NO_MATCH;

  const known = kind ? kinds.find((k) => k.kind === kind) : undefined;
  const refused = kind && kind !== 'favourites' && !CATEGORIES_OF[kind].some((c) => allowed.has(c)) ? known : undefined;
  const showHints = (fieldOpen || query.length > 0) && (!parsed || parsed.kind === 'menu' || parsed.kind === 'unknown');

  return {
    line: {
      entries,
      status,
      emptyText,
      errorText: failureText(failed, entries.length),
      onRetry: retry,
      onPick: (pick: AddLinePick) => (pick.kind === 'item' && pick.category === 'diagnosis' ? onPickDiagnosis(pick) : onPickOrder(pick)),
        // 25 Oct (Andrew): the keyboard works as the menu footer says. The first row is highlighted on open, ↑ ↓ and the mouse
      // move the highlight, Enter adds the highlighted row (this replaces the #769 click-only rule), Esc closes. Tab first
      // closes the list and says so, so focus never leaves the open list without a word. While the line is "Searching…"
      // Enter waits: the rows on screen may be the previous text's answer.
      tabClosesList: true,
      holdEnterWhileSearching: true,
    },
    below: (
      <>
        {refused ? (
          <p data-slot="ap-slash-refused" role="status" className="m-0 text-size-sm text-text-secondary">
            {slashCannotAdd(refused.label)}
          </p>
        ) : null}
        {showHints ? <SlashHintGrid kinds={kinds} unknownWord={parsed?.kind === 'unknown' ? parsed.word : undefined} /> : null}
      </>
    ),
  };
}
